/**
 * Orders the Editor's Picks list so most of it reflects the categories the
 * user already favors, while leaving room for games they'd never pick.
 *
 * Nothing here is latched: the list is recomputed as favorites and dismissals
 * change, so hearting a row visibly rearranges what's suggested next.
 */

import { games } from '../data/games';

const gamesById = new Map(games.map(game => [game.id, game]));

export const PICKS_LIMIT = 7;

// Which of the top three categories owns which slot. Interleaved rather than
// grouped so the list doesn't read as three blocks, and deliberately sparse -
// slots 2 and 6 are never reserved, so every list keeps some discovery in it.
const RESERVED_SLOTS = [
  { slot: 0, rank: 0 },
  { slot: 1, rank: 1 },
  { slot: 3, rank: 2 },
  { slot: 4, rank: 0 },
  { slot: 5, rank: 1 },
];

/**
 * The categories a user favors most, strongest first, capped at three. Ties
 * break to the lower category id. Page-break sentinels and ids with no matching
 * game don't resolve, so they never reach the count.
 * @param {(number|string)[]} favorites
 * @returns {number[]} up to three category ids
 */
export const topCategories = (favorites = []) => {
  const counts = new Map();

  favorites.forEach(id => {
    const category = gamesById.get(id)?.category;
    if (!category) return;
    counts.set(category, (counts.get(category) || 0) + 1);
  });

  return [...counts.entries()]
    .sort(([aCategory, aCount], [bCategory, bCount]) => bCount - aCount || aCategory - bCategory)
    .slice(0, 3)
    .map(([category]) => category);
};

/**
 * Builds the ordered suggestion list. Each reserved slot takes the next unused
 * game from its category in the pool's own order; everything else fills from
 * the pool top down.
 *
 * A reserved slot whose category is missing or already drained is left to that
 * top-down fill rather than shifting the other reservations up - the
 * arrangement is positional, not a ranking. A user with no favorites reserves
 * nothing and gets the pool's first `limit` games, which is what this list was
 * before suggestions existed.
 *
 * @param {Object} options
 * @param {number[]} options.poolIds Candidate ids, in the order they should fall back to
 * @param {(number|string)[]} [options.favorites]
 * @param {number[]} [options.unliked]
 * @param {number|null} [options.excludeId] The hero's game, when there is one
 * @param {number} [options.limit]
 * @returns {number[]} game ids, at most `limit` of them
 */
export const buildSuggestions = ({
  poolIds,
  favorites = [],
  unliked = [],
  excludeId = null,
  limit = PICKS_LIMIT,
}) => {
  const available = poolIds.filter(id =>
    id !== excludeId &&
    !favorites.includes(id) &&
    !unliked.includes(id) &&
    gamesById.has(id)
  );

  const ranked = topCategories(favorites);
  const used = new Set();
  const slots = new Array(limit).fill(null);

  // Reservations are claimed before the fill, so a category pick can't be
  // spent on a generic slot first.
  RESERVED_SLOTS.forEach(({ slot, rank }) => {
    const category = ranked[rank];
    if (slot >= limit || !category) return;

    const id = available.find(
      candidate => !used.has(candidate) && gamesById.get(candidate).category === category
    );
    if (!id) return;

    slots[slot] = id;
    used.add(id);
  });

  const leftovers = available.filter(id => !used.has(id));
  let next = 0;

  for (let slot = 0; slot < limit; slot += 1) {
    if (slots[slot] !== null || next >= leftovers.length) continue;
    slots[slot] = leftovers[next];
    next += 1;
  }

  return slots.filter(id => id !== null);
};
